import React, { useState } from 'react';
import { StickyNote, Trash2, GripVertical } from 'lucide-react';
import { Note } from '../../types';

interface NoteListProps {
  notes: Note[];
  onSelectNote: (id: string) => void;
  onDeleteNote: (id: string) => void;
  onReorder: (notes: Note[]) => void;
}

export default function NoteList({ notes, onSelectNote, onDeleteNote, onReorder }: NoteListProps) {
  const [draggedNote, setDraggedNote] = useState<Note | null>(null);
  const [dragOverNoteId, setDragOverNoteId] = useState<string | null>(null);

  const getTitle = (content: string) => {
    const div = document.createElement('div');
    div.innerHTML = content;
    const h2 = div.querySelector('h2');
    return h2?.textContent || 'Untitled Note';
  };

  const getPreview = (content: string) => {
    const div = document.createElement('div');
    div.innerHTML = content;
    // Remove the title (h2) from preview
    const h2 = div.querySelector('h2');
    if (h2) h2.remove();
    // Get text content and truncate
    const text = div.textContent || '';
    return text.length > 100 ? text.slice(0, 100) + '...' : text;
  };

  const handleDragStart = (e: React.DragEvent, note: Note) => {
    setDraggedNote(note);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, noteId: string) => {
    e.preventDefault();
    if (draggedNote?.id !== noteId) {
      setDragOverNoteId(noteId);
    }
  };

  const handleDrop = (e: React.DragEvent, targetNote: Note) => {
    e.preventDefault();
    if (!draggedNote || draggedNote.id === targetNote.id) return;

    const oldIndex = notes.findIndex(n => n.id === draggedNote.id);
    const newIndex = notes.findIndex(n => n.id === targetNote.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedNotes = [...notes];
    reorderedNotes.splice(oldIndex, 1);
    reorderedNotes.splice(newIndex, 0, draggedNote);

    onReorder(reorderedNotes);
    setDraggedNote(null);
    setDragOverNoteId(null);
  };

  const handleDragEnd = () => {
    setDraggedNote(null);
    setDragOverNoteId(null);
  };

  // Array of pastel background colors
  const bgColors = [
    'bg-blue-50',
    'bg-green-50',
    'bg-yellow-50',
    'bg-pink-50',
    'bg-purple-50',
    'bg-indigo-50',
    'bg-red-50',
    'bg-orange-50'
  ];

  return (
    <div className="space-y-2 p-4">
      {notes.map((note, index) => (
        <div
          key={note.id}
          draggable
          onDragStart={(e) => handleDragStart(e, note)}
          onDragOver={(e) => handleDragOver(e, note.id)}
          onDrop={(e) => handleDrop(e, note)}
          onDragEnd={handleDragEnd}
          className={`
            ${bgColors[index % bgColors.length]} 
            p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow 
            group relative
            ${dragOverNoteId === note.id ? 'border-2 border-indigo-500' : ''}
            ${draggedNote?.id === note.id ? 'opacity-50' : ''}
          `}
        >
          <div className="flex items-center gap-2">
            <div 
              className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <GripVertical className="w-4 h-4" />
            </div>
            <div 
              className="flex-1 cursor-pointer"
              onClick={() => onSelectNote(note.id)}
            >
              <h3 className="font-medium text-gray-900 mb-2">
                {getTitle(note.content)}
              </h3>
              <p className="text-sm text-gray-600 line-clamp-2">
                {getPreview(note.content)}
              </p>
              <div className="text-xs text-gray-500 mt-2">
                {new Date(note.updated_at).toLocaleDateString()}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteNote(note.id);
              }}
              className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              title="Delete note"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}